// PowerScheduleEngine.js
//
// FE Engine Pattern V2 (rk=beeca47db94541d594bc4aa830f62672) — initial fetch + SignalR
// OnPowerSchedulesUpdated (array of changed schedules only) → addOrUpdate per rk.
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import powerScheduleService from '@/services/PowerScheduleService';
import responseHelper from '../../../services/ResponseHelper';
import powerScheduleCache from './PowerScheduleCache';
import { POWER_SCHEDULE_CONFLICT } from './powerScheduleConst';

const _name = 'PowerScheduleEngine';

class PowerScheduleEngine extends WssEventHandlerBase {
    _name = _name;

    bootup() {
        super.bootup();
        this.clear();
        this._unsubMessage = this._wsNotifier.subscribe(events.OnPowerSchedulesUpdated, this.onMessage);
        this._unsubRemoved = this._wsNotifier.subscribe(events.OnPowerSchedulesDeleted, this.onRemoved);
    }

    clear() {
        super.clear();
        if (this._unsubMessage) this._unsubMessage();
        if (this._unsubRemoved) this._unsubRemoved();
    }

    /**
     * BE push — changed schedules only.
     * @param {object[]} items
     */
    onMessage = (items) => {
        try {
            powerScheduleCache.addOrUpdate(items ?? []);
            this._notify();
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    onRemoved = async (items) => {
        try {
            items.forEach((x) => {
                powerScheduleCache.delete(x.rk);
            });

            this._notify();

            return true;
        } catch (error) {
            this.logError('remove', error.message);
            return false;
        }
    };

    /**
     * Initial fetch — all schedules via REST.
     * @returns {Promise<object[]>}
     */
    fetch = async () => {
        try {
            const res = await powerScheduleService.getSchedules();
            const items = res?.data?.items ?? [];
            powerScheduleCache.setCache([...items]);
            this._notify();
            return items;
        } catch (error) {
            this.logError('fetch', error.message);
            return [];
        }
    };

    /**
     * Creates a schedule, cache follows via SignalR push.
     * @param {object} data - { did, vendor, plugIn, plugOut, mode, type, power, start, end }
     * @returns {Promise<number>} > 0 ok, POWER_SCHEDULE_CONFLICT on overlap, -1 on error
     */
    add = async (data) => {
        try {
            const resp = await powerScheduleService.postSchedule(data);
            const r = responseHelper.check(resp, '', _name);
            if (r === POWER_SCHEDULE_CONFLICT || resp?.response?.status === 409) return POWER_SCHEDULE_CONFLICT;
            return r < 0 ? -1 : r;
        } catch (error) {
            this.logError('add', error.message);
            return -1;
        }
    };

    /**
     * Deletes a schedule — push has no delete signal yet, so remove locally + refetch.
     * @param {object} schedule - full PowerSchedule DTO
     * @returns {Promise<boolean>} success
     */
    remove = async (schedule) => {
        try {
            const resp = await powerScheduleService.deleteSchedule(schedule);
            if (responseHelper.check(resp, '', _name) < 0) return false;
            powerScheduleCache.delete(schedule.rk);
            // await this.fetch();
            return true;
        } catch (error) {
            this.logError('remove', error.message);
            return false;
        }
    };

    /**
     * @param {string} did - battery rk
     * @returns {object[]}
     */
    getByDid = (did) => powerScheduleCache.getByDid(did);

    _notify = () => {
        ComponentNotifier.notify(_name, 'dataChanged', {});
    };
}

const powerScheduleEngine = new PowerScheduleEngine();
export default powerScheduleEngine;
